import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Dialog, Transition, Menu } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  KeyIcon,
  FolderIcon,
  LinkIcon,
  UsersIcon,
  UserPlusIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  UserCircleIcon,
  ChevronDownIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  ArrowDownTrayIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import Breadcrumbs from './Breadcrumbs';
import GlobalSearchModal from './GlobalSearchModal';
import GlobalSearchTrigger from './GlobalSearchTrigger';
import NotificationBell from './NotificationBell';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Companies', href: '/companies', icon: BuildingOffice2Icon },
  { name: 'Credentials', href: '/credentials', icon: KeyIcon },
  { name: 'Folders', href: '/folders', icon: FolderIcon },
  { name: 'Share Links', href: '/share-links', icon: LinkIcon },
  { name: 'Knowledge Base', href: '/kb', icon: BookOpenIcon },
];

const adminNavigation = [
  { name: 'Users', href: '/admin/users', icon: UsersIcon },
  { name: 'Invites', href: '/admin/invites', icon: UserPlusIcon },
  { name: 'Audit Logs', href: '/admin/audit-logs', icon: ClipboardDocumentListIcon },
  { name: 'CPanel Sync', href: '/admin/cpanel-sync', icon: ServerStackIcon },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isAdmin } = usePermissions();

  // Global keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Sidebar content (shared between mobile and desktop)
  const SidebarContent = ({ onLinkClick }: { onLinkClick?: () => void }) => (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border-r border-gray-200 px-6 pb-4">
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center border-b border-gray-200 -mx-6 px-6">
        <img className="h-10 w-auto" src="/assets/promatics-logo.png" alt="Promatics" />
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          {/* Main navigation */}
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href ||
                  (item.href !== '/' && location.pathname.startsWith(item.href));
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={onLinkClick}
                      className={classNames(
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                        'group flex gap-x-3 rounded-md px-3 py-2 text-sm font-medium transition-colors'
                      )}
                    >
                      <item.icon
                        className={classNames(
                          isActive
                            ? 'text-primary-600'
                            : 'text-gray-400 group-hover:text-gray-500',
                          'h-5 w-5 shrink-0'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Admin section */}
          {isAdmin && (
            <li>
              <button
                onClick={() => setAdminExpanded(!adminExpanded)}
                className="flex w-full items-center justify-between text-xs font-semibold leading-6 text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
              >
                <span>Admin</span>
                <ChevronDownIcon
                  className={classNames(
                    'h-4 w-4 transition-transform',
                    adminExpanded ? 'rotate-180' : ''
                  )}
                />
              </button>
              <ul
                role="list"
                className={classNames(
                  '-mx-2 mt-2 space-y-1 overflow-hidden transition-all duration-200',
                  adminExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                )}
              >
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={onLinkClick}
                        className={classNames(
                          isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                          'group flex gap-x-3 rounded-md px-3 py-2 text-sm font-medium transition-colors'
                        )}
                      >
                        <item.icon
                          className={classNames(
                            isActive
                              ? 'text-primary-600'
                              : 'text-gray-400 group-hover:text-gray-500',
                            'h-5 w-5 shrink-0'
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          )}

          {/* Bottom section */}
          <li className="mt-auto -mx-2">
            <Link
              to="/settings"
              onClick={onLinkClick}
              className={classNames(
                location.pathname === '/settings'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                'group flex gap-x-3 rounded-md px-3 py-2 text-sm font-medium transition-colors'
              )}
            >
              <Cog6ToothIcon
                className={classNames(
                  location.pathname === '/settings'
                    ? 'text-primary-600'
                    : 'text-gray-400 group-hover:text-gray-500',
                  'h-5 w-5 shrink-0'
                )}
                aria-hidden="true"
              />
              Settings
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  );

  return (
    <>
      <div>
        {/* Mobile sidebar */}
        <Transition.Root show={sidebarOpen}>
          <Dialog<"div"> as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <Transition.Child
             
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
               
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                   
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  <SidebarContent onLinkClick={() => setSidebarOpen(false)} />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
          <SidebarContent />
        </div>

        <div className="lg:pl-64">
          {/* Top bar */}
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            <div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="flex flex-1 items-center">
                <GlobalSearchTrigger onClick={() => setSearchOpen(true)} />
              </div>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <NotificationBell />
                <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" aria-hidden="true" />
                <Menu<"div"> as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="sr-only">Open user menu</span>
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium text-sm">
                        {user?.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        className="ml-3 text-sm font-medium leading-6 text-gray-900"
                        aria-hidden="true"
                      >
                        {user?.name}
                      </span>
                      <ChevronDownIcon className="ml-2 h-4 w-4 text-gray-400" />
                    </span>
                  </Menu.Button>
                  <Transition
                   
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-56 origin-top-right rounded-lg bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <Menu.Item>
                        {({ active }: { active: boolean }) => (
                          <Link
                            to="/profile"
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'flex items-center gap-3 px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <UserCircleIcon className="h-5 w-5 text-gray-400" />
                            Profile
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }: { active: boolean }) => (
                          <Link
                            to="/settings"
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'flex items-center gap-3 px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <Cog6ToothIcon className="h-5 w-5 text-gray-400" />
                            Settings
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }: { active: boolean }) => (
                          <Link
                            to="/export"
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'flex items-center gap-3 px-4 py-2 text-sm text-gray-700'
                            )}
                          >
                            <ArrowDownTrayIcon className="h-5 w-5 text-gray-400" />
                            Export Data
                          </Link>
                        )}
                      </Menu.Item>
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <Menu.Item>
                          {({ active }: { active: boolean }) => (
                            <button
                              onClick={handleLogout}
                              className={classNames(
                                active ? 'bg-gray-50' : '',
                                'flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-400" />
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          {/* Main content */}
          <main className="py-6 lg:py-8">
            <div className="px-4 sm:px-6 lg:px-8">
              <Breadcrumbs />
              {children}
            </div>
          </main>
        </div>
      </div>

      {/* Global Search Modal */}
      <GlobalSearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
